/**
 * @module agent/adapters/services/RetryHandler
 *
 * AWS command retry handler with exponential backoff.
 * Encapsulates retry logic previously in AgentCoreAdapter.
 *
 * Responsibilities:
 * - Execute AWS commands with automatic retry
 * - Detect retryable errors (throttling, server errors)
 * - Apply exponential backoff between retries
 *
 * @example
 * const retryHandler = new RetryHandler(awsClient, { maxRetries: 3 });
 * const response = await retryHandler.executeWithRetry(command);
 */

/**
 * Non-retryable AWS error names.
 * These indicate permanent failures that shouldn't be retried.
 */
const NON_RETRYABLE_ERRORS = new Set([
  'ValidationException',
  'ResourceNotFoundException',
  'AccessDeniedException',
  'InvalidParameterException',
  'InvalidRequestException'
]);

/**
 * Retryable AWS error names.
 * These indicate transient failures that may succeed on retry.
 */
const RETRYABLE_ERRORS = new Set([
  'ThrottlingException',
  'InternalServerException',
  'ServiceUnavailableException'
]);

/**
 * RetryHandler for AWS SDK commands with exponential backoff.
 */
class RetryHandler {
  /**
   * @param {Object} client - AWS SDK client instance with send method
   * @param {Object} [options] - Configuration options
   * @param {number} [options.maxRetries=3] - Maximum number of retry attempts
   * @param {number} [options.baseDelay=1000] - Base delay in milliseconds for backoff
   * @throws {TypeError} If client is missing or lacks send() method
   */
  constructor(client, options = {}) {
    // Fix #3: Fail-fast validation for client
    if (!client || typeof client.send !== 'function') {
      throw new TypeError('RetryHandler requires a client with send() method');
    }

    this._client = client;
    this.maxRetries = options.maxRetries ?? 3;
    this.baseDelay = options.baseDelay ?? 1000;
  }

  /**
   * Execute AWS command with automatic retry on transient failures.
   *
   * @param {Object} command - AWS SDK command to execute
   * @param {Object} [options] - Per-call options that override instance defaults
   * @param {number} [options.maxRetries] - Override max retries for this call
   * @returns {Promise<Object>} Command response
   * @throws {Error} Last error if all retries exhausted
   */
  async executeWithRetry(command, options = {}) {
    const maxRetries = options.maxRetries ?? this.maxRetries;
    const { abortSignal } = options;
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // TD-2347: bail immediately if the user already stopped the turn
      if (abortSignal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      try {
        // Only pass the options arg when there's a signal, so callers without an
        // abortSignal keep the original single-arg send() call shape.
        const response = abortSignal
          ? await this._client.send(command, { abortSignal })
          : await this._client.send(command);
        return response;
      } catch (error) {
        lastError = error;

        if (import.meta.env?.DEV) {
          console.error(`[RetryHandler] Attempt ${attempt}/${maxRetries} failed:`, {
            name: error.name,
            message: error.message,
            status: error.$metadata?.httpStatusCode
          });
        }

        // Check if error is retryable and we have retries left
        if (!RetryHandler.isRetryableError(error) || attempt === maxRetries) {
          break;
        }

        // Fix #5: Exponential backoff with jitter to prevent thundering herd
        // Base: 2^(attempt-1) * baseDelay, then apply 50-100% jitter
        // Per AWS/Google Cloud best practices for distributed retry
        // Attempt 1: ~1000-2000ms, Attempt 2: ~2000-4000ms, Attempt 3: ~4000-8000ms
        const baseBackoff = Math.pow(2, attempt - 1) * this.baseDelay;
        // NOSONAR: Math.random() is intentionally used for retry jitter timing.
        // This is NOT a security context - jitter prevents thundering herd in
        // distributed systems per AWS/Google Cloud retry best practices.
        // Cryptographic randomness is unnecessary for delay timing.
        const jitter = 0.5 + Math.random() * 0.5; // NOSONAR - safe for timing jitter
        const delay = baseBackoff * jitter;
        await this._sleep(delay);
      }
    }

    throw lastError;
  }

  /**
   * Check if an error is retryable.
   * Instance method that delegates to static for convenience.
   *
   * @param {Error} error - Error to check
   * @returns {boolean} True if error is retryable
   */
  isRetryableError(error) {
    return RetryHandler.isRetryableError(error);
  }

  /**
   * Check if an error is retryable.
   * Static method for use without instance.
   *
   * Fix #8: Precedence order (checked in this exact sequence):
   * 1. Non-retryable error names → immediate false (ValidationException, etc.)
   * 2. Retryable error names → immediate true (ThrottlingException, etc.)
   * 3. HTTP status codes: 429 → true, 5xx → true, 4xx → false
   * 4. Unknown errors → default false
   *
   * Retryable conditions:
   * - ThrottlingException (rate limiting)
   * - InternalServerException (server errors)
   * - HTTP status 429 (rate limit)
   * - HTTP status >= 500 (server errors)
   *
   * Non-retryable:
   * - ValidationException
   * - ResourceNotFoundException
   * - AccessDeniedException
   * - HTTP status 4xx (except 429)
   *
   * @param {Error} error - Error to check
   * @returns {boolean} True if error is retryable
   */
  static isRetryableError(error) {
    if (!error) {
      return false;
    }

    // TD-2347: a user-initiated abort is terminal — never auto-retry after Stop.
    if (error.name === 'AbortError') {
      return false;
    }

    const errorName = error.name;
    const httpStatusCode = error.$metadata?.httpStatusCode;

    // Check for explicitly non-retryable error names first
    if (errorName && NON_RETRYABLE_ERRORS.has(errorName)) {
      return false;
    }

    // Check for retryable error names
    if (errorName && RETRYABLE_ERRORS.has(errorName)) {
      return true;
    }

    // Check HTTP status codes
    if (httpStatusCode !== undefined) {
      // 429 = Rate limited (retryable)
      if (httpStatusCode === 429) {
        return true;
      }

      // 5xx = Server errors (retryable)
      if (httpStatusCode >= 500) {
        return true;
      }

      // 4xx (except 429) = Client errors (not retryable)
      if (httpStatusCode >= 400 && httpStatusCode < 500) {
        return false;
      }
    }

    // Default: not retryable for unknown errors
    return false;
  }

  /**
   * Sleep for specified duration.
   * @private
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default RetryHandler;
